import React from 'react';

/**
 * Highlights all occurrences of the search string in the text (case insensitive)
 * by wrapping them in <strong> tags.
 *
 * @param {string} text - The text to highlight
 * @param {string} searchString - The search string to highlight
 * @returns {React.ReactNode} A React node with highlighted search terms
 */
export const highlightSearch = (text, searchString) => {
  // If no search string or no text, return the text as-is
  if (!searchString || !text || typeof text !== 'string') {
    return text;
  }

  const searchLower = searchString.toLowerCase();
  const textLower = text.toLowerCase();
  const parts = [];
  let lastIndex = 0;
  let currentIndex = textLower.indexOf(searchLower, lastIndex);
  let matchIndex = 0;

  while (currentIndex !== -1) {
    // Add text before the match (preserving all characters including spaces)
    if (currentIndex > lastIndex) {
      const textBefore = text.substring(lastIndex, currentIndex);
      parts.push(<span key={`text-${lastIndex}`}>{textBefore}</span>);
    }

    // Add the matched text wrapped in <strong>
    parts.push(
      <strong key={`match-${matchIndex}-${currentIndex}`}>
        {text.substring(currentIndex, currentIndex + searchString.length)}
      </strong>,
    );

    lastIndex = currentIndex + searchString.length;
    currentIndex = textLower.indexOf(searchLower, lastIndex);
    matchIndex++;
  }

  // Add remaining text after the last match (preserving all characters including spaces)
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    parts.push(<span key={`text-${lastIndex}`}>{textAfter}</span>);
  }

  // If no matches were found, return the original text
  if (parts.length === 0) {
    return text;
  }

  // Wrap in span with white-space preservation to prevent collapsing
  // Using pre-wrap preserves spaces while still allowing text wrapping
  return <span style={{ whiteSpace: 'pre-wrap' }}>{parts}</span>;
};
